/**
 * TaskPreviewDrawer — right-rail preview for the currently-selected task
 * (previewStore). Editable: title, the four quick fields (status/priority/
 * due/person), and the markdown description. Shares the useTask cache with
 * the detail page so edits reflect in the originating row immediately.
 *
 * Unsaved-description guard: while the description editor holds unsaved edits
 * the drawer marks the store `dirty`. Any attempt to leave — switching task,
 * closing the drawer, or a route change — is intercepted and a dialog offers
 * to save, discard, or keep editing. The description editor is keyed by task
 * id so switching tasks always reloads the right description (never a stale
 * one).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useBlocker } from '@tanstack/react-router';
import * as Popover from '@radix-ui/react-popover';
import { Avatar, Icon, PriorityDot, StatusChip } from '@k-os/ui';
import {
  StatusEditor, PriorityEditor, DueEditor, ScheduledEditor, PersonEditor, SearchablePickerList,
  STATUS_LABELS, PRIORITY_LABELS,
} from './task-fields';
import { MarkdownDescription, type MarkdownDescriptionHandle } from './MarkdownDescription';
import { ConfirmDialog } from './finance/ConfirmDialog';
import { useTask, usePatchTask, useCommentOnTask, type TaskEventDto } from '../api/tasks';
import { useAreas } from '../api/areas';
import { usePreviewStore } from '../lib/preview-store';
import styles from './TaskPreviewDrawer.module.css';

export function TaskPreviewDrawer({ taskId }: { taskId: string }) {
  const close = usePreviewStore((s) => s.close);
  const setDirty = usePreviewStore((s) => s.setDirty);
  const pendingTaskId = usePreviewStore((s) => s.pendingTaskId);
  const pendingClose = usePreviewStore((s) => s.pendingClose);
  const confirmPending = usePreviewStore((s) => s.confirmPending);
  const cancelPending = usePreviewStore((s) => s.cancelPending);
  const navigate = useNavigate();
  const taskQ = useTask(taskId);
  const patch = usePatchTask();
  const comment = useCommentOnTask();
  const areasQ = useAreas(false);
  const task = taskQ.data?.task;

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  // Lets the unsaved-changes dialog save the in-progress description without
  // the user having to go back and press ⌘↵ first.
  const descRef = useRef<MarkdownDescriptionHandle>(null);
  useEffect(() => { setEditingTitle(false); setEditingDesc(false); setCommentDraft(''); }, [taskId]);

  // Block route navigation (sidebar, Open full, back/forward, tab close) while
  // the description has unsaved edits. Reads live store state so it can't go
  // stale between renders.
  const blocker = useBlocker({
    shouldBlockFn: () => usePreviewStore.getState().dirty,
    enableBeforeUnload: () => usePreviewStore.getState().dirty,
    withResolver: true,
  });

  const confirmOpen =
    blocker.status === 'blocked' || pendingTaskId != null || pendingClose;

  // Let the blocked intent (route change / task switch / close) through.
  const proceed = () => {
    if (blocker.status === 'blocked') {
      setDirty(false);
      blocker.proceed();
    } else {
      confirmPending();
    }
  };
  const handleDiscard = () => proceed();
  // `save()` runs the editor's own save path (PATCH + clear dirty), then the
  // stashed intent continues — the PATCH finishes in the background.
  const handleSaveAndContinue = () => {
    descRef.current?.save();
    proceed();
  };
  const handleKeepEditing = () => {
    if (blocker.status === 'blocked') blocker.reset();
    else cancelPending();
  };

  if (taskQ.isLoading) return <div className={styles.root}><div className={styles.loading}>Loading…</div></div>;
  if (taskQ.isError || !task) return <div className={styles.root}><div className={styles.error}>Couldn’t load task.</div></div>;

  const apply = (p: Parameters<typeof patch.mutate>[0]['patch']) => patch.mutate({ id: task.id, patch: p });

  // Events come newest-first; take the most recent 1–2 comments for the drawer.
  const recentComments = (taskQ.data?.events ?? [])
    .filter((e) => e.kind === 'commented')
    .slice(0, 2);

  const submitComment = () => {
    const body = commentDraft.trim();
    if (!body) return;
    comment.mutate({ id: task.id, body }, { onSuccess: () => setCommentDraft('') });
  };

  return (
    <div className={styles.root}>
      <div className={styles.bar}>
        <span>Preview</span>
        <span className={styles.spacer} />
        <button className={styles.linkBtn} onClick={() => navigate({ to: '/tasks/item/$id', params: { id: task.id } })}>
          <Icon name="arrowRight" size={12} /> Open full
        </button>
        <button className={styles.linkBtn} aria-label="Close preview" onClick={() => close()}>
          <Icon name="x" size={13} />
        </button>
      </div>

      {editingTitle ? (
        <input
          className={styles.titleInput}
          autoFocus
          value={titleDraft}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={() => { const t = titleDraft.trim(); if (t && t !== task.title) apply({ title: t }); setEditingTitle(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { const t = titleDraft.trim(); if (t && t !== task.title) apply({ title: t }); setEditingTitle(false); }
            if (e.key === 'Escape') setEditingTitle(false);
          }}
        />
      ) : (
        <div className={styles.title} role="button" tabIndex={0}
          onClick={() => { setTitleDraft(task.title); setEditingTitle(true); }}>
          {task.title}
        </div>
      )}

      <div className={styles.fields}>
        {/* Created and Source lead the grid as plain labels, not chips: both
            are read-only provenance (`sourceKind` isn't in the PATCH schema),
            so they shouldn't carry the editable chips' affordances. */}
        <div className={styles.fieldLabel} title={new Date(task.createdAt).toLocaleString()}>
          <span className={styles.fieldLabelKey}>Created</span>
          <span className={styles.fieldLabelValue}>{fullDate(task.createdAt)}</span>
        </div>
        <div className={styles.fieldLabel} title={task.sourceRef ?? undefined}>
          <span className={styles.fieldLabelKey}>Source</span>
          <span className={`${styles.fieldLabelValue} ${styles.sourceValue}`}>
            {task.sourceKind ? task.sourceKind.replace(/_/g, ' ') : '—'}
          </span>
        </div>
        <FieldPopover content={(close) => <StatusEditor task={task} onClose={close} />}>
          <StatusChip status={task.status} /> {STATUS_LABELS[task.status]}
        </FieldPopover>
        <FieldPopover content={(close) => <PriorityEditor task={task} onClose={close} />}>
          <PriorityDot priority={task.priority} /> {PRIORITY_LABELS[task.priority]}
        </FieldPopover>
        <FieldPopover content={(close) => <DueEditor task={task} onClose={close} />}>
          {task.dueAt ? `Due · ${shortDate(task.dueAt)}` : 'Due · —'}
        </FieldPopover>
        <FieldPopover content={(close) => <ScheduledEditor task={task} onClose={close} />}>
          {task.scheduledAt ? `Scheduled · ${shortDate(task.scheduledAt)}` : 'Scheduled · —'}
        </FieldPopover>
        <FieldPopover content={(close) => {
          // Patch via the drawer's stable `apply` (not a self-contained
          // editor): the editor popover unmounts the instant you pick, and in
          // React Query v5 that skips the mutation's onSuccess cache
          // invalidation — so the inherited context never refetched. Running
          // it from the always-mounted drawer mirrors the detail page and the
          // context is applied + reflected reliably. Picking an area that
          // carries a context auto-fills it (same inheritance as detail).
          const list = areasQ.data?.areas ?? [];
          return (
            <SearchablePickerList
              options={list.map((a) => ({ id: a.id, label: a.name, sub: a.standard }))}
              activeId={task.areaId}
              allowClear
              onPick={(id) => {
                const picked = id ? list.find((a) => a.id === id) ?? null : null;
                const inheritedContextId = picked?.contextId ?? null;
                apply(inheritedContextId ? { areaId: id, contextId: inheritedContextId } : { areaId: id });
                close();
              }}
            />
          );
        }}>
          {task.area ? `Area · ${task.area.name}` : 'Area · —'}
        </FieldPopover>
        <FieldPopover content={(close) => <PersonEditor task={task} onClose={close} />}>
          {task.person ? <><Avatar person={{ initials: task.person.initials, color: task.person.color }} size={16} /> {task.person.name}</> : 'Person · —'}
        </FieldPopover>
      </div>

      <div>
        <div className={styles.seclabel}>Description</div>
        <MarkdownDescription
          key={task.id}
          ref={descRef}
          value={task.description}
          editable={editingDesc}
          onRequestEdit={() => setEditingDesc(true)}
          onCancelEdit={() => { setDirty(false); setEditingDesc(false); }}
          onDirtyChange={setDirty}
          onSave={(md) => { apply({ description: md }); setDirty(false); setEditingDesc(false); }}
        />
      </div>

      <div>
        <div className={styles.seclabel}>Comments</div>
        {/* Form sits above the list: comments are newest-first, so the
            composer belongs next to the most recent one. */}
        <form
          className={styles.commentForm}
          onSubmit={(ev) => { ev.preventDefault(); submitComment(); }}
        >
          <input
            className={styles.commentInput}
            placeholder="Add a comment…"
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                submitComment();
              }
            }}
          />
          <span className="kos-kbd" aria-hidden>⌘↵</span>
          <button
            type="submit"
            className="kos-btn kos-btn-primary"
            disabled={!commentDraft.trim() || comment.isPending}
          >
            Post
          </button>
        </form>
        {recentComments.length === 0 ? (
          <div className={styles.commentsEmpty}>No comments yet.</div>
        ) : (
          <div className={styles.comments}>
            {recentComments.map((e) => <CommentCard key={e.id} event={e} />)}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Unsaved changes"
        message="Your description edits haven’t been saved yet."
        confirmLabel="Save changes"
        cancelLabel="Keep editing"
        extraAction={{ label: 'Discard changes', onClick: handleDiscard, destructive: true }}
        onConfirm={handleSaveAndContinue}
        onClose={handleKeepEditing}
      />
    </div>
  );
}

function CommentCard({ event }: { event: TaskEventDto }) {
  const date = new Date(event.createdAt);
  const actor = eventActorLabel(event);
  const payload = event.payload as Record<string, unknown> | null;
  const body = typeof payload?.body === 'string' ? payload.body : '';
  return (
    <div className={styles.commentCard}>
      <div className={styles.commentHead}>
        <strong>{actor}</strong>
        <span className={styles.commentTime}>
          {weekday(date)} {pad(date.getHours())}:{pad(date.getMinutes())}
        </span>
      </div>
      <div className={styles.commentBody}>{body}</div>
    </div>
  );
}

function eventActorLabel(event: TaskEventDto): string {
  const label = event.actorLabel?.trim();
  if (label) return label;
  if (event.actorKind === 'agent') return 'Agent';
  if (event.actorKind === 'system') return 'System';
  return 'User';
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `DD MMM` — e.g. `15 Jun`. Built explicitly so the order is ours, not the
 *  browser locale's (which renders `Jun 15` for en-US). */
function shortDate(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getDate())} ${MONTHS_SHORT[d.getMonth()]!}`;
}

/** `DD-MM-YYYY` — e.g. `18-09-2026`. */
function fullDate(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

function weekday(d: Date): string {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]!;
}

function FieldPopover({ content, children }: { content: (close: () => void) => ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className={styles.fieldChip}>{children}</button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content side="bottom" align="start" sideOffset={6} className={styles.popover}>
          {content(() => setOpen(false))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
